import { create } from "zustand";
import { isDesktop } from "react-device-detect";

interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface PwaState {
    canInstall: boolean;
    dismissed: boolean;
    dismiss: () => void;
    install: () => void;
}

interface PwaInternalState extends PwaState {
    promptEvent?: BeforeInstallPromptEvent;
}

const DISMISSED_UNTIL_KEY = "pwa_dismissed_until";

export const usePwaStore = create<PwaInternalState>((set, get) => {
    const handler = (e: Event) => {
        e.preventDefault();
        set({ promptEvent: e as BeforeInstallPromptEvent, canInstall: true });
    };
    window.addEventListener("beforeinstallprompt", handler);

    return {
        canInstall: false,
        dismissed: Date.now() < Number(localStorage.getItem(DISMISSED_UNTIL_KEY) ?? 0),
        dismiss: () => {
            // Dismiss the banner for 1 day on desktop, for 10 minutes on mobile
            localStorage.setItem(DISMISSED_UNTIL_KEY, String(Date.now() + (isDesktop ? 86400000 : 600000)));

            set({ dismissed: true });
        },
        install: async () => {
            const { promptEvent } = get();
            if (!promptEvent) {
                return;
            }

            await promptEvent.prompt();
            const { outcome } = await promptEvent.userChoice;
            if (outcome === "accepted") {
                set({ canInstall: false, dismissed: true });
            }
        },
    };
});
